import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-extract-review>` says about itself.
 *
 * Extract turns a document into a typed field patch. Two rules in the architecture decide whether
 * the result is reviewable at all, and both are structural in this piece: a value the target field
 * cannot hold is returned in an explicit `unmapped` list with its source span and is NEVER coerced
 * into the nearest legal one, and per-field confidence rides along so the uncertain can be sorted to
 * the top.
 *
 * Three decisions worth knowing before placing it.
 *
 * The unmapped list is a headed SECTION with its count in the summary line, not a footnote. The
 * failure being guarded against is the one that looks like success: a complete-looking patch where
 * three values were quietly rounded to something the document never said.
 *
 * A field with no confidence reported is NOT scored zero. Unscored rows sort after the scored ones
 * and read “not scored”, because pinning them to the top for ever while nothing is wrong is how a
 * list like this gets ignored.
 *
 * It does not accept or discard. Acceptance is an ordinary save by the accepting human and review is
 * the platform's existing diff surface; a second Accept button here would be a lesser review surface
 * for the same proposal, and the lesser one is the one that ships.
 */
const docs: ComponentDocs = {
  aliases: ['extraction review', 'extract', 'document extraction', 'field patch', 'unmapped values', 'confidence review'],

  answers: [
    'see what was pulled out of a document, least certain first',
    'see what the document said that no field would take',
    'find out why a value would not map onto its field',
    'decide whether an extraction is worth putting through review',
  ],

  placements: ['recordPage', 'appPage', 'screen'],

  whenToUse:
    'After a document has been extracted and before anybody decides what to do with the result — the ' +
    'screen that answers “is this worth reviewing”.',

  insteadUse: [
    {
      name: 'CompareView',
      when:
        'the decision is field-by-field accept or discard against the record’s current values. That is ' +
        'the proposal review surface; this one comes before it.',
    },
    {
      name: 'ValidationSummary',
      when: 'the problems are a save being refused rather than a document failing to map.',
    },
    {
      name: 'DataTable',
      when:
        'extracted values are one column among many being sorted, filtered and exported. A table ' +
        'compares values; this finds the doubtful ones.',
    },
  ],

  inputs: [
    {
      name: 'patch',
      type: 'json',
      description:
        'The extraction: `{ object, recordId?, values, confidence?, unmapped? }`, exactly the shape the ' +
        'shipped `ai_extract` prompt declares as its output. A property, because it is structured data. ' +
        'An ABSENT `unmapped` means the extractor did not say — which is weaker than “nothing failed to ' +
        'map”, and is drawn as such.',
    },
    {
      name: 'mappedHeading',
      attribute: 'mapped-heading',
      type: 'text',
      description: 'The heading over the fields the patch would set.',
      default: 'Fields this would set',
    },
    {
      name: 'unmappedHeading',
      attribute: 'unmapped-heading',
      type: 'text',
      description: 'The heading over the values that would not map.',
      default: 'Would not map — left for a person',
    },
    {
      name: 'emptyText',
      attribute: 'empty-text',
      type: 'text',
      description: 'What to say when the extraction produced neither a value nor an unmapped entry.',
      default: 'Nothing was extracted from this document.',
    },
  ],

  events: [
    {
      name: 'caos-extract-open-source',
      when: 'the source span of an unmapped value is pressed, and the extractor reported where it was',
      carries: '`{ field, source, at }` — `field` is null when the document said something no field claims',
    },
  ],

  state: [
    {
      name: 'rows',
      type: 'CaosExtractRow[]',
      description:
        'The mapped fields, least certain first: scored rows ascending, then unscored ones ' +
        'alphabetically. Read it to drive a work list elsewhere without recomputing the ordering rule.',
    },
    {
      name: 'unmapped',
      type: 'CaosExtractUnmapped[]',
      description: 'What would not map, filtered to the entries that actually carry a source and a reason.',
    },
    {
      name: 'blocking',
      type: 'boolean',
      description:
        'Anything would not map, or a mapped value is below 60% confidence. Read it to GATE sending the ' +
        'extraction onward — which is the difference between a review surface and a decorated one.',
    },
  ],

  parts: [
    { part: 'review', is: 'The outer column.' },
    { part: 'summary', is: 'The count line above both sections.' },
    { part: 'section', is: 'One of the two sections.' },
    { part: 'section-heading', is: 'A section’s heading.' },
    { part: 'row', is: 'One row in either section.' },
    { part: 'field', is: 'The field name in a row.' },
    { part: 'value', is: 'The extracted value.', when: 'the row is a mapped field' },
    { part: 'confidence', is: 'The reported confidence, or “not scored”.', when: 'the row is a mapped field' },
    { part: 'unmapped-row', is: 'A row in the would-not-map section.', when: 'something would not map' },
    { part: 'source', is: 'The source span, as a control that opens the document.', when: 'the row is an unmapped value' },
    { part: 'reason', is: 'Why the value would not map.', when: 'the row is an unmapped value' },
    { part: 'empty', is: 'The “nothing was extracted” line.', when: 'the extraction produced nothing at all' },
  ],

  accessibility:
    'The summary is a `role="status"` live region, so an extraction that arrives with values it could ' +
    'not map is announced rather than only drawn. Every row carries an `aria-label` reading the field, ' +
    'the value and the confidence in words — “confidence not scored” rather than a silence a reader ' +
    'would take for zero — and an unmapped row reads the field it was headed for, the text it came ' +
    'from and the reason. A value of null reads “cleared”, because an instruction to empty a field is ' +
    'a change and must not sound like a row that failed to draw. A source span with no reported ' +
    'position is an inert control rather than an absent one, so the text stays readable.',

  examples: [
    {
      key: 'all-mapped',
      label: 'Everything mapped',
      summary:
        'Four fields, all scored, ordered least certain first — so the 71% row is the first thing read ' +
        'and the 99% rows are where they belong. The summary says all mapped and none in doubt.',
      placement: {
        component: 'caos-extract-review',
        inputs: {
          patch: {
            object: 'invoice',
            recordId: 'inv_4410',
            values: { number: 'INV-4410', issued_on: '2026-09-30', total: 18400, currency: 'USD' },
            confidence: { number: 0.99, issued_on: 0.95, total: 0.99, currency: 0.71 },
          },
        },
      },
      stage: { needsWidth: 560 },
    },
    {
      key: 'would-not-map',
      label: 'Three values the fields would not take',
      summary:
        'The case this piece exists for. Each refused value keeps the text it came from and the reason ' +
        'the field would not take it, and nothing has been rounded to the nearest legal value — which ' +
        'is what would have made this look like a clean extraction.',
      placement: {
        component: 'caos-extract-review',
        inputs: {
          patch: {
            object: 'invoice',
            values: { number: 'INV-4411', issued_on: '2026-10-02' },
            confidence: { number: 0.97, issued_on: 0.88 },
            unmapped: [
              {
                field: 'payment_terms',
                source: 'net 45 days from receipt',
                reason: 'the field accepts net 15, net 30 or net 60, and nothing else',
                at: 812,
              },
              {
                field: 'total',
                source: '18,400.005',
                reason: 'the column holds two decimal places and this has three',
                at: 1044,
              },
              {
                source: 'delivered to the east yard',
                reason: 'no field on this object claims a delivery location',
              },
            ],
          },
        },
      },
      stage: { needsWidth: 560 },
    },
    {
      key: 'unscored-and-cleared',
      label: 'An unscored field, and one being emptied',
      summary:
        'Two cases a naive list gets wrong. `reference` has no reported confidence and reads “not ' +
        'scored” at the END, rather than sorting to the top as a zero. `notes` is a null, which is an ' +
        'instruction to clear the field, and it reads “cleared” rather than as an empty cell somebody ' +
        'would approve without seeing.',
      placement: {
        component: 'caos-extract-review',
        inputs: {
          patch: {
            object: 'invoice',
            values: { number: 'INV-4412', reference: 'PO-77120', notes: null },
            confidence: { number: 0.93, notes: 0.44 },
          },
        },
      },
      stage: { needsWidth: 560 },
    },
    {
      key: 'nothing-extracted',
      label: 'Nothing came out of the document',
      summary:
        'No values and no unmapped entries. Said in a line rather than drawn as two empty headed ' +
        'sections, which would read as a surface that has not finished loading.',
      placement: {
        component: 'caos-extract-review',
        inputs: { patch: { object: 'invoice', values: {} } },
      },
      stage: { needsWidth: 480 },
    },
  ],
};

export default docs;
